import React from 'react'
import clsx from 'clsx'
import { Trash } from 'iconsax-react'
import { TextareaAutosize, useTheme } from '@mui/material'
import { useDrop } from 'react-dnd'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'

import { ObjectType } from '../../../types'

import FlowField from '../FlowField'

import { ITEM_TYPE } from '../../../CustomField'
import { cleanFlow } from './helpers'
import { arraysEqual, moveItem } from '@SonarCX/utilities'
import { deleteFlow, updateFlow } from '@SonarCX/slices/customFields/flows'
import { selectCustomFields } from '@SonarCX/slices/customFields'
import { useAreYouSure } from '@SonarCX/providers/AreYouSureProvider'
import { useFlowTarget } from '../providers/FlowTargetProvider'
import useBaseStyles from '../../GroupSettings/Group/styles'
import useStyles from './styles'

type FlowProps = {
  flow: APIFlow
  isFirst: boolean
  isLast: boolean
  tab: ObjectType
}

const Flow: React.FC<FlowProps> = ({ flow, isFirst, isLast, tab }) => {
  const theme = useTheme()

  const dispatch = useAppDispatch()

  const { customFields } = useAppSelector(selectCustomFields)

  const { setAction, setReason, setSubtext, setV2 } = useAreYouSure()
  const { target } = useFlowTarget()

  const handleDropAdd = React.useCallback(
    (item: any) => {
      if (flow.fields.some(existing => existing.id === item.id)) {
        return
      }

      const newFlow = cleanFlow(flow)
      newFlow.fields = [...newFlow.fields, { id: item.id, options: [] }]
      dispatch(updateFlow(flow.id, newFlow))
    },
    [flow, dispatch]
  )

  const handleCanDrop = React.useCallback(
    (item: any) => {
      const field = customFields.find(field => field.id === item.id)

      if (!field) {
        return false
      }
      if (field.field_type === 'flow' && field.config?.id === flow.id) {
        return false
      }

      return true
    },
    [customFields, flow]
  )

  const [{ isOver }, addDropRef] = useDrop({
    accept: ITEM_TYPE,
    drop: handleDropAdd,
    canDrop: handleCanDrop,
    collect: monitor => ({
      isOver: !!monitor.isOver()
    })
  })

  const baseClasses = useBaseStyles({ isOver })
  const classes = useStyles()

  const handleBlurSave = React.useCallback(
    (prop: string, val: string) => {
      const original = flow[prop]
      const newVal = val.trim()

      if ((prop === 'title' && newVal.length === 0) || newVal === original) {
        return
      }

      const newFlow = cleanFlow(flow)
      if (prop === 'description') {
        newFlow.description = newVal === '' ? null : newVal
      } else {
        newFlow[prop] = newVal
      }
      dispatch(updateFlow(flow.id, newFlow))
    },
    [flow, dispatch]
  )

  const handleDeleteField = React.useCallback(
    (fieldID: string) => {
      if (!flow.fields.some(existing => existing.id === fieldID)) {
        return
      }

      const newFlow = cleanFlow(flow)
      newFlow.fields = newFlow.fields.filter(item => item.id !== fieldID)
      dispatch(updateFlow(flow.id, newFlow))
    },
    [flow, dispatch]
  )

  const handleFlowDelete = React.useCallback(() => {
    setReason(`Delete ${flow.title}`)
    setSubtext('Are you sure you want to delete this flow?')
    setV2(true)
    setAction(() => {
      dispatch(deleteFlow(flow.id))
    })
  }, [setReason, setSubtext, setV2, setAction, flow, dispatch])

  const handleFieldMove = React.useCallback(
    (source: string, target: string, above: boolean) => {
      const moved = moveItem(
        flow.fields,
        source,
        target,
        above,
        field => field.id
      )

      if (arraysEqual(moved, flow.fields, true)) {
        return
      }

      const newFlow = cleanFlow(flow)
      newFlow.fields = moved
      dispatch(updateFlow(flow.id, newFlow))
    },
    [flow, dispatch]
  )

  return (
    <>
      <div className={baseClasses.dropZone} />

      <div
        id={`flow-${flow.id}`}
        className={clsx(
          baseClasses.root,
          classes.root,
          isLast && baseClasses.last,
          target === flow.id && classes.glowRoot
        )}
      >
        <div className={baseClasses.title}>
          <input
            placeholder='Flow title'
            defaultValue={flow.title}
            onBlur={e => handleBlurSave('title', e.target.value)}
            className={baseClasses.name}
          />

          <Trash
            onClick={handleFlowDelete}
            size={theme.size.base}
            className={baseClasses.closeIcon}
          />
        </div>

        <div className={baseClasses.descriptionArea}>
          <div className={baseClasses.label}>Description</div>

          <TextareaAutosize
            minRows={1}
            maxRows={5}
            placeholder='Flow description'
            defaultValue={flow.description}
            onBlur={e => handleBlurSave('description', e.target.value)}
            className={baseClasses.description}
          />
        </div>

        {flow.fields.length === 0 ? (
          <div
            ref={addDropRef}
            className={clsx(baseClasses.fieldsDropbox, baseClasses.noFields)}
          >
            Drag fields here to add
          </div>
        ) : (
          <>
            <div className={baseClasses.label}>Fields</div>

            <div className={baseClasses.fields}>
              {flow.fields.map((field, i) => (
                <FlowField
                  key={field.id}
                  tab={tab}
                  fieldID={field.id}
                  isFirst={i === 0}
                  isLast={i === flow.fields.length - 1}
                  onDelete={() => handleDeleteField(field.id)}
                  flow={flow}
                  onMove={handleFieldMove}
                />
              ))}

              <div
                ref={addDropRef}
                className={clsx(
                  baseClasses.fieldsDropbox,
                  baseClasses.addFields
                )}
              >
                Drag fields here to add
              </div>
            </div>
          </>
        )}
      </div>

      {isLast && <div className={baseClasses.dropZone} />}
    </>
  )
}

export default Flow
